import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Button from "../../components/button";
import { useAuth } from "../../context/authContext";
import { AuthProviderKind, startLinkTelegram } from "../../api/auth";
import { getAuthApiError } from "../../utils/auth-api-error";
import styles from "./style.module.css";

function LoginMethodsPage() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();

  const linked = useMemo(() => {
    const map = new Map<AuthProviderKind, boolean>();
    user?.linkedProviders.forEach((p) => map.set(p.provider, true));
    return map;
  }, [user?.linkedProviders]);

  const emailLinked = linked.get("email") === true;
  const telegramLinked = linked.get("telegram") === true;

  const [tgSubmitting, setTgSubmitting] = useState(false);
  const [tgError, setTgError] = useState<string | null>(null);

  const openTelegramLink = useCallback(async () => {
    setTgError(null);
    setTgSubmitting(true);
    const popup = window.open("about:blank", "_blank");
    try {
      const { deepLink } = await startLinkTelegram();
      if (popup) {
        popup.location.replace(deepLink);
      } else {
        window.location.href = deepLink;
      }
    } catch (e) {
      if (popup && !popup.closed) {
        popup.close();
      }
      setTgError(getAuthApiError(e, "Не удалось открыть Telegram. Попробуйте позже."));
    } finally {
      setTgSubmitting(false);
    }
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshMe();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshMe]);

  if (!user) return null;

  const telegramLabel = (() => {
    if (!telegramLinked) return null;
    if (user.username) return `@${user.username}`;
    if (user.telegramId != null) return `id: ${user.telegramId}`;
    return "Привязан";
  })();

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Способы входа</h1>
      </div>

      <section className={styles.row}>
        <div className={styles.rowHeader}>
          <div className={styles.rowTitle}>
            <h3>Email</h3>
            {emailLinked && user.email && <p className={styles.value}>{user.email}</p>}
          </div>
          {emailLinked ? (
            <Button label="Привязан" variant="secondary" disabled />
          ) : (
            <Button
              label="Привязать"
              variant="secondary"
              onClick={() => navigate("/account/login-methods/link-email")}
            />
          )}
        </div>
      </section>

      <section className={styles.row}>
        <div className={styles.rowHeader}>
          <div className={styles.rowTitle}>
            <h3>Telegram</h3>
            {telegramLinked && telegramLabel && <p className={styles.value}>{telegramLabel}</p>}
          </div>
          {telegramLinked ? (
            <Button label="Привязан" variant="secondary" disabled />
          ) : (
            <Button
              label="Привязать"
              variant="secondary"
              onClick={() => void openTelegramLink()}
              disabled={tgSubmitting}
              loading={tgSubmitting}
            />
          )}
        </div>
        {tgError && <p className={styles.error}>{tgError}</p>}
      </section>
    </div>
  );
}

export default LoginMethodsPage;
